import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, CheckCircle, Instagram, Facebook } from 'lucide-react';
import Button from './Button';
import { getFunctionsUrl } from '../utils/functions';
import './ContactPage.css';

// Helper function to submit contact form to Firestore

const submitContactFormToFirestore = async (contactData: any) => {
  try {
    const functionsUrl = await getFunctionsUrl();
    const response = await fetch(`${functionsUrl}/submitContactForm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData)
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to submit contact form');
    }

    return result;
  } catch (error) {
    console.error('Error submitting contact form to Firestore:', error);
    throw error;
  }
};

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
  phone: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.message) {
      setError('Email and message are required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await submitContactFormToFirestore(formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        phone: ''
      });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="contact-page">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="contact-success"
          >
            <CheckCircle size={64} />
            <h1>Message Sent Successfully!</h1>
            <p>Thank you for reaching out to us. We'll get back to you as soon as possible.</p>
            <Button 
              onClick={() => setIsSubmitted(false)}
              variant="primary"
              size="large"
            >
              Send Another Message
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact – Carly Faye Foundation</title>
        <meta name="description" content="Contact the Carly Faye Foundation with questions about scholarships, donations, or getting involved." />
        <link rel="canonical" href="https://carly-faye-foundation.web.app/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact – Carly Faye Foundation" />
        <meta property="og:description" content="Get in touch with the Carly Faye Foundation." />
        <meta property="og:url" content="https://carly-faye-foundation.web.app/contact" />
        <meta property="og:image" content="/images/cf-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact – Carly Faye Foundation" />
        <meta name="twitter:description" content="Contact us with questions or to get involved." />
        <meta name="twitter:image" content="/images/cf-logo.png" />
      </Helmet>
      <div className="contact-content">
        <div className="container">
                    <div className="contact-grid">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="contact-info"
            >
              <h2>Get in Touch</h2>
              <p>
                Have questions about our scholarships or want to learn more about how you can support 
                the Carly Faye Foundation? We'd love to hear from you.
              </p>
              
              <div className="contact-methods">
                <a href="mailto:info@carlyfayefoundation.org" className="contact-method">
                  <Mail size={24} />
                  <div>
                    <h3>Email</h3>
                    <p>info@carlyfayefoundation.org</p>
                  </div>
                </a>
                
                <a href="tel:(555) 123-4567" className="contact-method">
                  <Phone size={24} />
                  <div>
                    <h3>Phone</h3>
                    <p>(555) 123-4567</p>
                  </div>
                </a>
                
                <a href="https://www.instagram.com/carlyfayefoundation/" target="_blank" rel="noopener noreferrer" className="contact-method">
                  <Instagram size={24} />
                  <div>
                    <h3>Instagram</h3>
                    <p>@carlyfayefoundation</p>
                  </div>
                </a>
                
                <a href="https://www.facebook.com/profile.php?id=100064601145200" target="_blank" rel="noopener noreferrer" className="contact-method">
                  <Facebook size={24} />
                  <div>
                    <h3>Facebook</h3>
                    <p>Carly Faye Foundation</p>
                  </div>
                </a>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="contact-form-container"
            >
              <form onSubmit={handleSubmit} className="contact-form">
                <h2>Send us a Message</h2>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="What's this about?"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us how we can help..."
                    rows={6}
                    required
                  />
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  size="large"
                  icon={Send}
                  className="submit-button"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage; 